import React, { useEffect, useState } from 'react'

import { Strings, useTranslation } from 'language/translator'
import { useSnackbarActions } from 'redux/actions/Snackbar'
import { useCustomer } from 'redux/hooks/Customer'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { isMcpEnabledForCustomer } from 'helpers/Functions/isMcpEnabled'
import * as McpOAuthClientsApi from 'redux/api/McpOAuthClients'
import * as McpPlatformAccessApi from 'redux/api/McpPlatformAccess'

import { SnackbarMessage } from 'types/GlobalSnackbar'
import { EAccountRole } from 'types/GlobalAccount'
import {
  CreatedMcpOAuthClient,
  McpOAuthClient
} from 'types/GlobalMcpOAuthClient'
import { McpPlatform, McpPlatformAccess } from 'types/GlobalMcpPlatformAccess'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Step from 'components_new/atoms/Stepper/Step'
import StepContent from 'components_new/atoms/Stepper/StepContent'
import StepLabel from 'components_new/atoms/Stepper/StepLabel'
import Stepper from 'components_new/atoms/Stepper'
import Switch from 'components_new/atoms/Switch'
import Text from 'components_new/atoms/Text'

import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import SettingsTemplate from 'components_new/templates/SettingsTemplate'

// Local: dedicated MCP container on port 3991 (see docker-compose).
// Prod: mcp.homepal.se subdomain that points at the MCP Lambda.
const mcpBaseUrl = () =>
  typeof window !== 'undefined' && window.location.host.includes('localhost')
    ? 'http://localhost:3991'
    : 'https://mcp.homepal.se'

const mcpEndpoint = () => `${mcpBaseUrl()}/mcp`

const isLocal = () =>
  typeof window !== 'undefined' && window.location.host.includes('localhost')

/**
 * No one-click install/deep-link exists for adding custom MCP connectors
 * to claude.ai (verified against Anthropic's help center) — the flow is
 * manual: Customize > Connectors > + > Add custom connector. So we copy
 * the URL and open that page, same pattern as ChatGPT. On Team/Enterprise
 * plans an org Owner adds it under admin-settings/connectors instead.
 *
 * claude.ai connects from Anthropic's cloud, so the MCP URL must be
 * public HTTPS — localhost is never reachable.
 */
const claudeConnectorsUrl = () => 'https://claude.ai/customize/connectors'

// ChatGPT has no documented public deep-link for installing custom MCP
// connectors. Best we can do is open the Apps settings (the section was
// renamed from "Connectors" to "Apps" in late 2026).
const chatGptConnectorsUrl = () => 'https://chatgpt.com/#settings/Apps'

// Deep link straight into the admin center's add-connector view
// (Copilot → Connectors → Add). Verified working Aug 2026 — if Microsoft
// breaks the fragment route, fall back to https://admin.microsoft.com/
// and the click path in the step-2 caption.
const m365AdminCenterUrl = () =>
  'https://admin.cloud.microsoft/#/copilot/connectors/add'

// Direct link to the OAuth client registration tool in Teams Developer
// Portal — step 1 of the Copilot setup. (Do NOT create an "App" there;
// only Tools → OAuth client registration is needed.)
const teamsDevPortalOAuthUrl = () =>
  'https://dev.teams.microsoft.com/tools/oauth-configuration'

// The M365 Copilot chat web app, where users activate the source under
// Settings → Sources once the connector has propagated.
const m365CopilotChatUrl = () => 'https://m365.cloud.microsoft/chat'

// Lovable has no documented deep link to its connector settings; the guide
// describes the click path (Connectors → + → MCP server).
const lovableUrl = () => 'https://lovable.dev/'

// Platforms an admin allows per organisation, with the texts of their
// switch and of the dialog confirming that it is turned off.
const PLATFORM_ACCESS_TEXTS: Record<
  McpPlatform,
  {
    sectionTitle: Strings<'mcp_tokens'>
    sectionIntro: Strings<'mcp_tokens'>
    enabled: Strings<'mcp_tokens'>
    disabled: Strings<'mcp_tokens'>
    disableDialogTitle: Strings<'mcp_tokens'>
    disableDialogIntro: Strings<'mcp_tokens'>
  }
> = {
  lovable: {
    sectionTitle: 'lovable_section_title',
    sectionIntro: 'lovable_section_intro',
    enabled: 'lovable_enabled',
    disabled: 'lovable_disabled',
    disableDialogTitle: 'lovable_disable_dialog_title',
    disableDialogIntro: 'lovable_disable_dialog_intro'
  },
  'hub-systems': {
    sectionTitle: 'hub_systems_section_title',
    sectionIntro: 'hub_systems_section_intro',
    enabled: 'hub_systems_enabled',
    disabled: 'hub_systems_disabled',
    disableDialogTitle: 'hub_systems_disable_dialog_title',
    disableDialogIntro: 'hub_systems_disable_dialog_intro'
  }
}

const MCP_PLATFORMS: McpPlatform[] = ['lovable', 'hub-systems']

// OAuth issuer as advertised in our discovery docs (router.ts). Shown in
// the Copilot dialog for tenants doing a manual Teams Developer Portal
// OAuth registration instead of dynamic client registration.
const oauthIssuerUrl = () =>
  isLocal() ? 'http://localhost:3990' : 'https://api.homepal.se'

/**
 * Dev-tunnel detection. With MCP_OAUTH_ISSUER_URL set to a tunnel origin,
 * the local MCP container advertises it as OAuth issuer. External clients
 * (claude.ai, M365 Copilot) require public HTTPS, so a https issuer means
 * they can be pointed at `<issuer>/mcp` instead of localhost.
 */
const useDevTunnelBase = () => {
  const [base, setBase] = useState<string | null>(null)

  useEffect(() => {
    if (!isLocal()) return

    fetch(`${mcpBaseUrl()}/.well-known/oauth-authorization-server`)
      .then((res) => res.json())
      .then((doc: { issuer?: string }) => {
        if (doc.issuer && doc.issuer.startsWith('https://')) {
          setBase(doc.issuer.replace(/\/+$/, ''))
        }
      })
      .catch(() => undefined)
  }, [])

  return base
}

const McpTokensPage = () => {
  const { t } = useTranslation()
  const { toggleSnackbarOpen } = useSnackbarActions()

  const customer = useCustomer()
  const mcpEnabled = isMcpEnabledForCustomer(customer)

  const { account } = useAccountSettings()
  const isAdmin = account?.role === EAccountRole.ADMIN

  const [copilotHelpOpen, setCopilotHelpOpen] = useState(false)
  const [claudeWebHelpOpen, setClaudeWebHelpOpen] = useState(false)
  const [chatGptHelpOpen, setChatGptHelpOpen] = useState(false)
  const [lovableHelpOpen, setLovableHelpOpen] = useState(false)

  // Platforms that connect through dynamic client registration (Lovable,
  // Hub Systems) are allowed per organisation by an admin.
  const [platformAccess, setPlatformAccess]
    = useState<McpPlatformAccess | null>(null)
  const [savingPlatformAccess, setSavingPlatformAccess] = useState(false)
  const [platformToDisable, setPlatformToDisable]
    = useState<McpPlatform | null>(null)
  const isPlatformEnabled = (platform: McpPlatform) =>
    platformAccess?.platforms.includes(platform) ?? false
  const lovableEnabled = isPlatformEnabled('lovable')

  // The customer's single OAuth credential pair. Loaded for everyone (the
  // guides show the client_id); create/rotate/revoke is admin-only.
  const [client, setClient] = useState<McpOAuthClient | null>(null)
  const [creatingClient, setCreatingClient] = useState(false)
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false)
  const [createdClient, setCreatedClient]
    = useState<CreatedMcpOAuthClient | null>(null)
  const [clientToDelete, setClientToDelete]
    = useState<McpOAuthClient | null>(null)

  const tunnelBase = useDevTunnelBase()
  const publicMcpEndpoint = tunnelBase ? `${tunnelBase}/mcp` : mcpEndpoint()

  useEffect(() => {
    if (!mcpEnabled) return

    McpOAuthClientsApi.getOne()
      .then((res) => setClient(res.data.data))
      .catch(() => undefined)

    McpPlatformAccessApi.getAll()
      .then((res) => setPlatformAccess(res.data.data))
      .catch(() => undefined)
  }, [mcpEnabled])

  const copy = (value: string) => {
    navigator.clipboard.writeText(value)
    toggleSnackbarOpen(SnackbarMessage.CopiedToClipboard)
  }

  const handleGenerateClient = () => {
    setRotateConfirmOpen(false)
    setCreatingClient(true)
    McpOAuthClientsApi.createOrReplace()
      .then((res) => {
        const created = res.data.data

        setCreatedClient(created)
        setClient({
          id: created.id,
          label: created.label,
          client_id: created.client_id,
          last_used_at: null,
          created_at: created.created_at
        })
      })
      .catch(() => undefined)
      .finally(() => setCreatingClient(false))
  }

  const handleDeleteClient = () => {
    if (!clientToDelete) return

    const { id } = clientToDelete

    setClientToDelete(null)
    McpOAuthClientsApi.destroy(id)
      .then(() => setClient(null))
      .catch(() => undefined)
  }

  const handleSetPlatformEnabled = (
    platform: McpPlatform,
    enabled: boolean
  ) => {
    setPlatformToDisable(null)
    setSavingPlatformAccess(true)
    McpPlatformAccessApi.setEnabled(platform, enabled)
      .then((res) => setPlatformAccess(res.data.data))
      .catch(() => undefined)
      .finally(() => setSavingPlatformAccess(false))
  }

  const togglePlatform = (platform: McpPlatform) => {
    if (isPlatformEnabled(platform)) {
      setPlatformToDisable(platform)
    } else {
      handleSetPlatformEnabled(platform, true)
    }
  }

  const connectToClaudeWeb = () => {
    setClaudeWebHelpOpen(true)
  }

  const connectToChatGpt = () => {
    setChatGptHelpOpen(true)
  }

  if (!mcpEnabled) {
    return (
      <SettingsTemplate>
        <SectionHeader
          gutterBottom={true}
          title={t('mcp_tokens', 'page_title')}
        />
        <Text color="text.secondary" sx={{ mb: 2 }}>
          {t('mcp_tokens', 'not_available')}
        </Text>
      </SettingsTemplate>
    )
  }

  return (
    <>
      <SettingsTemplate>
        <SectionHeader
          gutterBottom={true}
          title={t('mcp_tokens', 'page_title')}
        />

        <Text color="text.secondary" sx={{ mb: 2 }}>
          {t('mcp_tokens', 'page_intro')}
        </Text>

        {isLocal() && !tunnelBase && (
          <Text color="warning.main" sx={{ fontSize: 13, mb: 2 }}>
            {t('mcp_tokens', 'localhost_warning')}
          </Text>
        )}

        {isLocal() && tunnelBase && (
          <Text color="success.main" sx={{ fontSize: 13, mb: 2 }}>
            {t('mcp_tokens', 'tunnel_active')} <code>{publicMcpEndpoint}</code>
          </Text>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <Button
            disabled={isLocal() && !tunnelBase}
            onClick={connectToClaudeWeb}
            startIcon={<Icon name="AI" />}
            variant="contained"
          >
            {t('mcp_tokens', 'connect_claude_web')}
          </Button>
          <Button
            onClick={connectToChatGpt}
            startIcon={<Icon name="AI" />}
            variant="outlined"
          >
            {t('mcp_tokens', 'connect_chatgpt')}
          </Button>
          <Button
            onClick={() => setCopilotHelpOpen(true)}
            startIcon={<Icon name="AI" />}
            variant="outlined"
          >
            {t('mcp_tokens', 'connect_copilot')}
          </Button>
          <Button
            onClick={() => setLovableHelpOpen(true)}
            startIcon={<Icon name="AI" />}
            variant="outlined"
          >
            {t('mcp_tokens', 'connect_lovable')}
          </Button>
        </Box>

        {/* The customer's single OAuth credential pair — managed by admins */}
        {isAdmin && (
          <>
            <SectionHeader
              gutterBottom={true}
              title={t('mcp_tokens', 'client_section_title')}
            />

            <Text color="text.secondary" sx={{ mb: 2 }}>
              {t('mcp_tokens', 'client_section_intro')}
            </Text>

            {client ? (
              <Box sx={{ mb: 3 }}>
                <CopyRow
                  label="Client ID"
                  onCopy={copy}
                  value={client.client_id}
                />
                <Text color="text.secondary" sx={{ fontSize: 12, mb: 1.5 }}>
                  {t('mcp_tokens', 'client_created_at')}{' '}
                  {new Date(client.created_at).toLocaleDateString('sv-SE')}
                  {' — '}
                  {t('mcp_tokens', 'client_secret_shown_once')}
                </Text>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    disabled={creatingClient}
                    onClick={() => setRotateConfirmOpen(true)}
                    size="small"
                    variant="outlined"
                  >
                    {t('mcp_tokens', 'client_rotate')}
                  </Button>
                  <Button
                    color="error"
                    onClick={() => setClientToDelete(client)}
                    size="small"
                    variant="text"
                  >
                    {t('mcp_tokens', 'client_revoke')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                disabled={creatingClient}
                onClick={handleGenerateClient}
                sx={{ mb: 3 }}
                variant="contained"
              >
                {t('mcp_tokens', 'client_generate')}
              </Button>
            )}

            {MCP_PLATFORMS.map((platform) => {
              const texts = PLATFORM_ACCESS_TEXTS[platform]
              const enabled = isPlatformEnabled(platform)

              return (
                <React.Fragment key={platform}>
                  <SectionHeader
                    gutterBottom={true}
                    title={t('mcp_tokens', texts.sectionTitle)}
                  />

                  <Text color="text.secondary" sx={{ mb: 1 }}>
                    {t('mcp_tokens', texts.sectionIntro)}
                  </Text>

                  <Box
                    sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 3 }}
                  >
                    <Switch
                      checked={enabled}
                      disabled={savingPlatformAccess || platformAccess === null}
                      onChange={() => togglePlatform(platform)}
                    />
                    <Text>
                      {enabled
                        ? t('mcp_tokens', texts.enabled)
                        : t('mcp_tokens', texts.disabled)}
                    </Text>
                  </Box>
                </React.Fragment>
              )
            })}
          </>
        )}
      </SettingsTemplate>

      {/* Stäng av en plattform — befintliga anslutningar återkallas */}
      {platformToDisable && (
        <SimpleDialog
          actions={
            <>
              <Button onClick={() => setPlatformToDisable(null)} variant="text">
                {t('global', 'cancel')}
              </Button>
              <Button
                color="error"
                disabled={savingPlatformAccess}
                onClick={() =>
                  handleSetPlatformEnabled(platformToDisable, false)
                }
                variant="contained"
              >
                {t('mcp_tokens', 'platform_disable')}
              </Button>
            </>
          }
          onClose={() => setPlatformToDisable(null)}
          open={true}
          title={t(
            'mcp_tokens',
            PLATFORM_ACCESS_TEXTS[platformToDisable].disableDialogTitle
          )}
        >
          <Text color="text.secondary">
            {t(
              'mcp_tokens',
              PLATFORM_ACCESS_TEXTS[platformToDisable].disableDialogIntro
            )}
          </Text>
        </SimpleDialog>
      )}

      {/* Rotera klientuppgifter — gamla slutar fungera direkt */}
      <SimpleDialog
        actions={
          <>
            <Button onClick={() => setRotateConfirmOpen(false)} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={creatingClient}
              onClick={handleGenerateClient}
              variant="contained"
            >
              {t('mcp_tokens', 'client_rotate')}
            </Button>
          </>
        }
        onClose={() => setRotateConfirmOpen(false)}
        open={rotateConfirmOpen}
        title={t('mcp_tokens', 'client_rotate_dialog_title')}
      >
        <Text color="text.secondary">
          {t('mcp_tokens', 'client_rotate_dialog_intro')}
        </Text>
      </SimpleDialog>

      {/* Visa skapade uppgifter — secret visas bara en gång */}
      <SimpleDialog
        actions={
          <Button onClick={() => setCreatedClient(null)} variant="contained">
            {t('global', 'close')}
          </Button>
        }
        fullWidth={true}
        maxWidth="md"
        onClose={() => setCreatedClient(null)}
        open={createdClient !== null}
        title={t('mcp_tokens', 'client_created_dialog_title')}
      >
        <Text color="text.secondary" sx={{ mb: 2 }}>
          {t('mcp_tokens', 'client_created_dialog_intro')}
        </Text>
        {createdClient && (
          <Box sx={{ mb: 1 }}>
            <CopyRow
              label="Client ID"
              onCopy={copy}
              value={createdClient.client_id}
            />
            <CopyRow
              label="Client secret"
              onCopy={copy}
              value={createdClient.client_secret}
            />
          </Box>
        )}
      </SimpleDialog>

      {/* Ta bort klientuppgifter */}
      <DeleteDialog
        onClose={() => setClientToDelete(null)}
        onDelete={(cb) => {
          handleDeleteClient()
          cb()
        }}
        open={clientToDelete !== null}
        title={clientToDelete?.label ?? ''}
        type={t('mcp_tokens', 'client_delete_type')}
      />

      {/* Microsoft 365 Copilot setup */}
      <CopilotHelpDialog
        customerClientId={client?.client_id ?? null}
        onClose={() => setCopilotHelpOpen(false)}
        onCopy={copy}
        open={copilotHelpOpen}
        tunnelBase={tunnelBase}
      />

      {/* Claude (web) setup */}
      <ClaudeWebHelpDialog
        customerClientId={client?.client_id ?? null}
        endpoint={publicMcpEndpoint}
        onClose={() => setClaudeWebHelpOpen(false)}
        onCopy={copy}
        open={claudeWebHelpOpen}
      />

      {/* ChatGPT setup */}
      <ChatGptHelpDialog
        customerClientId={client?.client_id ?? null}
        endpoint={publicMcpEndpoint}
        onClose={() => setChatGptHelpOpen(false)}
        onCopy={copy}
        open={chatGptHelpOpen}
        showLocalhostWarning={isLocal() && !tunnelBase}
      />

      {/* Lovable setup */}
      <LovableHelpDialog
        enabled={lovableEnabled}
        endpoint={publicMcpEndpoint}
        onClose={() => setLovableHelpOpen(false)}
        onCopy={copy}
        open={lovableHelpOpen}
        showLocalhostWarning={isLocal() && !tunnelBase}
      />
    </>
  )
}

interface LovableHelpDialogProps {
  open: boolean
  endpoint: string
  showLocalhostWarning: boolean
  onClose: () => void
  onCopy: (value: string) => void

  // Whether an admin has allowed Lovable for the organisation. Lovable
  // registers itself (no client credentials), so this switch is the only
  // organisation-level gate.
  enabled: boolean
}

const LovableHelpDialog = (props: LovableHelpDialogProps) => {
  const {
    open, endpoint, showLocalhostWarning, onClose, onCopy, enabled
  } = props
  const { t } = useTranslation()

  const openLovable = () => {
    // Copy the URL right before jumping so it is guaranteed to be in the
    // clipboard when the user lands in Lovable.
    onCopy(endpoint)
    window.open(lovableUrl(), '_blank', 'noopener,noreferrer')
  }

  return (
    <SimpleDialog
      actions={
        <Button onClick={onClose} variant="contained">
          {t('global', 'close')}
        </Button>
      }
      fullWidth={true}
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={t('mcp_tokens', 'lovable_dialog_title')}
    >
      <Text color="text.secondary" sx={{ mb: 2 }}>
        {t('mcp_tokens', 'lovable_dialog_intro')}
      </Text>

      {!enabled && (
        <Text color="warning.main" sx={{ fontSize: 13, mb: 2 }}>
          {t('mcp_tokens', 'lovable_not_enabled_warning')}
        </Text>
      )}

      {showLocalhostWarning && (
        <Text color="warning.main" sx={{ fontSize: 13, mb: 2 }}>
          {t('mcp_tokens', 'localhost_warning')}
        </Text>
      )}

      <Stepper activeStep={-1} orientation="vertical">
        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'lovable_step_1')}
          </StepLabel>
          <StepContent>
            <EndpointBox endpoint={endpoint} onCopy={onCopy} />
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'lovable_step_2')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'lovable_step_2_item_1'),
                t('mcp_tokens', 'lovable_step_2_item_2'),
                t('mcp_tokens', 'lovable_step_2_item_3'),
                t('mcp_tokens', 'lovable_step_2_item_4')
              ]}
            />
            <Button
              endIcon={<Icon fontSize="small" name="OpenInNew" />}
              onClick={openLovable}
              size="small"
              sx={{ mb: 2 }}
              variant="outlined"
            >
              {t('mcp_tokens', 'lovable_open')}
            </Button>
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'lovable_step_3')}
          </StepLabel>
          <StepContent>
            <StepItems items={[t('mcp_tokens', 'lovable_step_3_item_1')]} />
            <Text color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
              {t('mcp_tokens', 'lovable_step_3_note')}
            </Text>
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'lovable_step_4')}
          </StepLabel>
          <StepContent>
            <StepItems items={[t('mcp_tokens', 'lovable_step_4_item_1')]} />
            <Text color="text.secondary" sx={{ fontSize: 13 }}>
              {t('mcp_tokens', 'lovable_step_4_note')}
            </Text>
          </StepContent>
        </Step>
      </Stepper>
    </SimpleDialog>
  )
}

interface ChatGptHelpDialogProps {
  open: boolean
  endpoint: string
  showLocalhostWarning: boolean
  onClose: () => void
  onCopy: (value: string) => void

  // The customer's generated client_id — when present, the guide instructs
  // choosing "Custom OAuth client" as registration method with these
  // credentials (and client_secret_post so the secret is actually sent).
  customerClientId: string | null
}

const ChatGptHelpDialog = (props: ChatGptHelpDialogProps) => {
  const {
    open, endpoint, showLocalhostWarning, onClose, onCopy, customerClientId
  } = props
  const { t } = useTranslation()

  const openChatGpt = () => {
    // Copy the URL right before jumping so it is guaranteed to be in the
    // clipboard when the user lands in ChatGPT's settings.
    onCopy(endpoint)
    window.open(chatGptConnectorsUrl(), '_blank', 'noopener,noreferrer')
  }

  return (
    <SimpleDialog
      actions={
        <Button onClick={onClose} variant="contained">
          {t('global', 'close')}
        </Button>
      }
      fullWidth={true}
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={t('mcp_tokens', 'chatgpt_dialog_title')}
    >
      <Text color="text.secondary" sx={{ mb: 2 }}>
        {t('mcp_tokens', 'chatgpt_dialog_intro')}
      </Text>

      {showLocalhostWarning && (
        <Text color="warning.main" sx={{ fontSize: 13, mb: 2 }}>
          {t('mcp_tokens', 'localhost_warning')}
        </Text>
      )}

      <Stepper activeStep={-1} orientation="vertical">
        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'chatgpt_step_1')}
          </StepLabel>
          <StepContent>
            <EndpointBox endpoint={endpoint} onCopy={onCopy} />
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'chatgpt_step_2')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'chatgpt_step_2_item_1'),
                t('mcp_tokens', 'chatgpt_step_2_item_2')
              ]}
            />
            <Button
              endIcon={<Icon fontSize="small" name="OpenInNew" />}
              onClick={openChatGpt}
              size="small"
              sx={{ mb: 2 }}
              variant="outlined"
            >
              {t('mcp_tokens', 'chatgpt_open_settings')}
            </Button>
            <Text color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
              {t('mcp_tokens', 'chatgpt_step_2_note')}
            </Text>
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'chatgpt_step_3')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'chatgpt_step_3_item_1'),
                t('mcp_tokens', 'chatgpt_step_3_item_2'),
                t('mcp_tokens', 'chatgpt_step_3_item_3'),
                t('mcp_tokens', 'chatgpt_step_3_item_4'),
                t('mcp_tokens', 'chatgpt_step_3_item_5')
              ]}
            />
            {customerClientId ? (
              <Box sx={{ mb: 2 }}>
                <CopyRow
                  label="Client ID"
                  onCopy={onCopy}
                  value={customerClientId}
                />
                <CopyRow
                  label="Client secret"
                  onCopy={onCopy}
                  value={t('mcp_tokens', 'copilot_secret_from_generation')}
                />
              </Box>
            ) : (
              <Text color="warning.main" sx={{ fontSize: 13, mb: 2 }}>
                {t('mcp_tokens', 'no_client_credentials_warning')}
              </Text>
            )}
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'chatgpt_step_4')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'chatgpt_step_4_item_1'),
                t('mcp_tokens', 'chatgpt_step_4_item_2')
              ]}
            />
            <Text color="text.secondary" sx={{ fontSize: 13 }}>
              {t('mcp_tokens', 'chatgpt_step_4_note')}
            </Text>
          </StepContent>
        </Step>
      </Stepper>
    </SimpleDialog>
  )
}

interface ClaudeWebHelpDialogProps {
  open: boolean
  endpoint: string
  onClose: () => void
  onCopy: (value: string) => void

  // The customer's generated client_id — when present, the guide instructs
  // filling in Advanced settings with it + the secret from generation time.
  customerClientId: string | null
}

const ClaudeWebHelpDialog = (props: ClaudeWebHelpDialogProps) => {
  const { open, endpoint, onClose, onCopy, customerClientId } = props
  const { t } = useTranslation()

  const openClaude = () => {
    // Copy the URL right before jumping so it is guaranteed to be in the
    // clipboard when the user lands in Claude's connector dialog.
    onCopy(endpoint)
    window.open(claudeConnectorsUrl(), '_blank', 'noopener,noreferrer')
  }

  return (
    <SimpleDialog
      actions={
        <Button onClick={onClose} variant="contained">
          {t('global', 'close')}
        </Button>
      }
      fullWidth={true}
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={t('mcp_tokens', 'claude_web_dialog_title')}
    >
      <Text color="text.secondary" sx={{ mb: 2 }}>
        {t('mcp_tokens', 'claude_web_dialog_intro')}
      </Text>

      <Stepper activeStep={-1} orientation="vertical">
        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'claude_web_step_1')}
          </StepLabel>
          <StepContent>
            <EndpointBox endpoint={endpoint} onCopy={onCopy} />
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'claude_web_step_2')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'claude_web_step_2_item_1'),
                t('mcp_tokens', 'claude_web_step_2_item_2'),
                t('mcp_tokens', 'claude_web_step_2_item_3')
              ]}
            />
            <Button
              endIcon={<Icon fontSize="small" name="OpenInNew" />}
              onClick={openClaude}
              size="small"
              sx={{ mb: 2 }}
              variant="outlined"
            >
              {t('mcp_tokens', 'claude_web_open_settings')}
            </Button>
            {customerClientId ? (
              <Box sx={{ mb: 2 }}>
                <CopyRow
                  label="Client ID"
                  onCopy={onCopy}
                  value={customerClientId}
                />
                <CopyRow
                  label="Client secret"
                  onCopy={onCopy}
                  value={t('mcp_tokens', 'copilot_secret_from_generation')}
                />
              </Box>
            ) : (
              <Text color="warning.main" sx={{ fontSize: 13, mb: 2 }}>
                {t('mcp_tokens', 'no_client_credentials_warning')}
              </Text>
            )}
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'claude_web_step_3')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'claude_web_step_3_item_1'),
                t('mcp_tokens', 'claude_web_step_3_item_2')
              ]}
            />
            <Text color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
              {t('mcp_tokens', 'claude_web_step_3_note')}
            </Text>
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'claude_web_step_4')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'claude_web_step_4_item_1'),
                t('mcp_tokens', 'claude_web_step_4_item_2')
              ]}
            />
            <Text color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
              {t('mcp_tokens', 'claude_web_step_4_note')}
            </Text>
          </StepContent>
        </Step>

        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'claude_web_step_5')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'claude_web_step_5_item_1'),
                t('mcp_tokens', 'claude_web_step_5_item_2')
              ]}
            />
            <Text color="text.secondary" sx={{ fontSize: 13 }}>
              {t('mcp_tokens', 'claude_web_step_5_note')}
            </Text>
          </StepContent>
        </Step>
      </Stepper>
    </SimpleDialog>
  )
}

// Optional guide screenshots. Drop files in frontend/public/mcp-guides/
// (e.g. copilot-tdp.png) and set the paths here — null hides the slot.
const COPILOT_GUIDE_IMAGES: {
  tdp: string | null
  connector: string | null
  rollout: string | null
  chat: string | null
} = {
  tdp: '/mcp-guides/copilot-tdp.png',
  connector: '/mcp-guides/copilot-connector.png',
  // TODO: retake after prod deploy — the connector pane still shows a
  // devtunnel MCP endpoint in this shot.
  rollout: '/mcp-guides/copilot-rollout.png',
  chat: '/mcp-guides/copilot-chat.png'
}

const GuideImage = ({ src }: { src: string | null }) =>
  src ? (
    <img
      alt=""
      src={src}
      style={{
        border: '1px solid rgba(128, 128, 128, 0.3)',
        borderRadius: 8,
        marginBottom: 16,
        width: '100%'
      }}
    />
  ) : null

// The guides render as always-expanded vertical steppers: every step is
// forced active so the numbered dots stay colored, and activeStep={-1}
// since there is no progression to track.
const stepLabelSx = {
  '& .MuiStepLabel-label': { fontWeight: 600 }
}

// Bullet list of short actions inside one guide step.
const StepItems = ({ items }: { items: string[] }) => (
  <Box
    component="ul"
    sx={{
      color: 'text.secondary',
      fontSize: 13,
      m: 0,
      mb: 2,
      pl: 2.5,
      '& li': { mb: 0.75 }
    }}
  >
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </Box>
)

// The MCP endpoint URL with a copy button.
const EndpointBox = ({
  endpoint,
  onCopy
}: {
  endpoint: string
  onCopy: (value: string) => void
}) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: 'action.hover',
      borderRadius: 1,
      display: 'flex',
      gap: 1,
      mb: 2,
      p: 1.5
    }}
  >
    <Box
      component="code"
      sx={{
        flex: 1,
        fontFamily: 'monospace',
        fontSize: 14,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {endpoint}
    </Box>
    <IconButton onClick={() => onCopy(endpoint)} size="small">
      <Icon fontSize="small" name="CopyAllOutlined" />
    </IconButton>
  </Box>
)

interface CopyRowProps {
  label: string
  value: string
  onCopy: (value: string) => void
}

const CopyRow = ({ label, value, onCopy }: CopyRowProps) => (
  <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 0.5 }}>
    <Text sx={{ fontSize: 13, minWidth: 170 }}>{label}</Text>
    <Box
      component="code"
      sx={{
        bgcolor: 'action.hover',
        borderRadius: 0.5,
        flex: 1,
        fontFamily: 'monospace',
        fontSize: 12,
        overflow: 'hidden',
        px: 1,
        py: 0.25,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}
    >
      {value}
    </Box>
    <IconButton onClick={() => onCopy(value)} size="small">
      <Icon fontSize="small" name="CopyAllOutlined" />
    </IconButton>
  </Box>
)

interface CopilotHelpDialogProps {
  open: boolean
  onClose: () => void
  onCopy: (value: string) => void
  tunnelBase: string | null

  // The customer's own generated client_id (admin section above). When
  // present, the guide shows it instead of the shared static fallback and
  // points the admin to the secret they got at generation time.
  customerClientId: string | null
}

const CopilotHelpDialog = (props: CopilotHelpDialogProps) => {
  const { open, onClose, onCopy, tunnelBase, customerClientId } = props
  const { t } = useTranslation()

  const endpoint = tunnelBase ? `${tunnelBase}/mcp` : mcpEndpoint()
  const issuer = tunnelBase ?? oauthIssuerUrl()

  // TDP "Base URL" is the allowlist of endpoints Teams sends the access token
  // to — it must be the MCP server host (mcp.homepal.se), NOT the OAuth issuer
  // (api.homepal.se). If it points at the issuer, Teams refuses to send the
  // token to the MCP endpoint, tool discovery fails with a "token store error"
  // and no request ever reaches /mcp. (In dev the tunnel serves both, so they
  // coincide.)
  const mcpBase = tunnelBase ?? mcpBaseUrl()

  // Field values for the Teams Developer Portal OAuth registration. Labels
  // match TDP's (English) UI verbatim so admins can map them 1:1.
  // Client credentials are enforced — the values are always the customer's
  // generated pair (validated by our /token).
  const tdpFields: { label: string; value: string }[] = [
    { label: 'Registration name', value: 'Homepal' },
    { label: 'Base URL', value: mcpBase },
    { label: 'Client ID', value: customerClientId ?? '—' },
    {
      label: 'Client secret',
      value: t('mcp_tokens', 'copilot_secret_from_generation')
    },
    { label: 'Authorization endpoint', value: `${issuer}/authorize` },
    { label: 'Token endpoint', value: `${issuer}/token` },
    { label: 'Refresh endpoint', value: `${issuer}/token` },
    { label: 'Scope', value: 'openid email profile' }
  ]

  return (
    <SimpleDialog
      actions={
        <Button onClick={onClose} variant="contained">
          {t('global', 'close')}
        </Button>
      }
      fullWidth={true}
      maxWidth="md"
      onClose={onClose}
      open={open}
      title={t('mcp_tokens', 'copilot_dialog_title')}
    >
      <Text color="text.secondary" sx={{ mb: 2 }}>
        {t('mcp_tokens', 'copilot_dialog_intro')}
      </Text>

      <Box
        sx={{
          border: 2,
          borderColor: 'warning.main',
          borderRadius: 1,
          mb: 2,
          p: 1.5
        }}
      >
        <Text color="warning.main" sx={{ fontSize: 13, fontWeight: 700 }}>
          {t('mcp_tokens', 'copilot_materialization_disclaimer')}
        </Text>
      </Box>

      {isLocal() && !tunnelBase && (
        <Text color="warning.main" sx={{ fontSize: 13, mb: 2 }}>
          {t('mcp_tokens', 'copilot_localhost_hint')}
        </Text>
      )}

      {!customerClientId && (
        <Text color="warning.main" sx={{ fontSize: 13, mb: 2 }}>
          {t('mcp_tokens', 'no_client_credentials_warning')}
        </Text>
      )}

      <Stepper activeStep={-1} orientation="vertical">
        {/* Steg 1: OAuth-registrering i Teams Developer Portal */}
        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'copilot_step_1')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'copilot_step_1_item_1'),
                t('mcp_tokens', 'copilot_step_1_item_2'),
                t('mcp_tokens', 'copilot_step_1_item_3'),
                t('mcp_tokens', 'copilot_step_1_item_4')
              ]}
            />
            <Button
              endIcon={<Icon fontSize="small" name="OpenInNew" />}
              onClick={() =>
                window.open(
                  teamsDevPortalOAuthUrl(),
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              size="small"
              sx={{ mb: 2 }}
              variant="outlined"
            >
              {t('mcp_tokens', 'copilot_open_tdp')}
            </Button>

            <Box sx={{ mb: 2 }}>
              {tdpFields.map((row) => (
                <CopyRow
                  key={row.label}
                  label={row.label}
                  onCopy={onCopy}
                  value={row.value}
                />
              ))}
            </Box>

            <Text sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
              {t('mcp_tokens', 'copilot_step_1_footnote_intro')}
            </Text>
            <StepItems
              items={[
                t('mcp_tokens', 'copilot_step_1_footnote_item_1'),
                t('mcp_tokens', 'copilot_step_1_footnote_item_2'),
                t('mcp_tokens', 'copilot_step_1_footnote_item_3'),
                t('mcp_tokens', 'copilot_step_1_footnote_item_4')
              ]}
            />

            <GuideImage src={COPILOT_GUIDE_IMAGES.tdp} />
          </StepContent>
        </Step>

        {/* Steg 2: Skapa connectorn i admin center */}
        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'copilot_step_2')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'copilot_step_2_item_1'),
                t('mcp_tokens', 'copilot_step_2_item_2'),
                t('mcp_tokens', 'copilot_step_2_item_3'),
                t('mcp_tokens', 'copilot_step_2_item_4'),
                t('mcp_tokens', 'copilot_step_2_item_5'),
                t('mcp_tokens', 'copilot_step_2_item_6')
              ]}
            />
            <Button
              endIcon={<Icon fontSize="small" name="OpenInNew" />}
              onClick={() =>
                window.open(
                  m365AdminCenterUrl(),
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              size="small"
              sx={{ mb: 2 }}
              variant="outlined"
            >
              {t('mcp_tokens', 'copilot_open_admin_center')}
            </Button>

            <Box sx={{ mb: 1 }}>
              <CopyRow label="Display name" onCopy={onCopy} value="Homepal" />
              <CopyRow
                label="MCP endpoint URL"
                onCopy={onCopy}
                value={endpoint}
              />
            </Box>

            <Text sx={{ fontSize: 13, fontWeight: 600, mb: 2 }}>
              {t('mcp_tokens', 'copilot_reference_id_hint')}
            </Text>

            <GuideImage src={COPILOT_GUIDE_IMAGES.connector} />
          </StepContent>
        </Step>

        {/* Steg 3: Rulla ut till användarna */}
        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'copilot_step_3')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'copilot_step_3_item_1'),
                t('mcp_tokens', 'copilot_step_3_item_2'),
                t('mcp_tokens', 'copilot_step_3_item_3'),
                t('mcp_tokens', 'copilot_step_3_item_4')
              ]}
            />
            <Text color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
              {t('mcp_tokens', 'copilot_step_3_note')}
            </Text>

            <GuideImage src={COPILOT_GUIDE_IMAGES.rollout} />
          </StepContent>
        </Step>

        {/* Steg 4: Aktivera källan i chatten */}
        <Step active={true} expanded={true}>
          <StepLabel sx={stepLabelSx}>
            {t('mcp_tokens', 'copilot_step_4')}
          </StepLabel>
          <StepContent>
            <StepItems
              items={[
                t('mcp_tokens', 'copilot_step_4_item_1'),
                t('mcp_tokens', 'copilot_step_4_item_2'),
                t('mcp_tokens', 'copilot_step_4_item_3')
              ]}
            />
            <Button
              endIcon={<Icon fontSize="small" name="OpenInNew" />}
              onClick={() =>
                window.open(
                  m365CopilotChatUrl(),
                  '_blank',
                  'noopener,noreferrer'
                )
              }
              size="small"
              sx={{ mb: 2 }}
              variant="outlined"
            >
              {t('mcp_tokens', 'copilot_open_chat')}
            </Button>

            <Text color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
              {t('mcp_tokens', 'copilot_step_4_note_1')}
            </Text>
            <Text color="text.secondary" sx={{ fontSize: 13, mb: 2 }}>
              {t('mcp_tokens', 'copilot_step_4_note_2')}
            </Text>

            <GuideImage src={COPILOT_GUIDE_IMAGES.chat} />
          </StepContent>
        </Step>
      </Stepper>
    </SimpleDialog>
  )
}

export default McpTokensPage
